import _ from "utilities/vendor/lodash/4.13.1/js/lodash.custom.min";
import Utilities from "utilities/global/sparta-events-utility/2.3.3/js/utilities";
/* global _ Utilities */

// Regex for escaping event names.
const regex = /[(.)+([)+(\])+]/gi;

// Replacement characters for regex matches.
const replacementChars = {
  '.': '/',
  '[': '(',
  ']': ')',
};

const EVENTS = {
  SPA_PARAMS_LOADED: 'SPA_PARAMS_LOADED.spartaEventsUtility',
  APP_CONTAINER_LOADED: 'APP_CONTAINER_LOADED.spartaEventsUtility',
  APP_CONTEXT_LOADED: 'APP_CONTEXT_LOADED.spartaEventsUtility',
};

/**
 * Pub-sub for app events. Singleton.
 */
class SpartaEventsUtility {
  /**
   * Sets up an instance of the utility.
   */
  constructor() {
    this.subscriptions = {};
    this.alreadyBeenPublished = {};
  }

  /**
   * Returns the events object.
   * @return {object} Events object.
   */
  get EVENTS() {
    return EVENTS;
  }

  /**
   * Listens for a custom event.
   * @param {string} event - This can be namespaced with a `:` like
   *   `SPARTA_GLOBAL_READY.spartaBootstrapUtility:foo`.
   * @param {function} callback - A fuction to be executed upon completion of the event.
   * @param {boolean} retroactive - Whether to trigger immediate callback when the event has already
   *   been published before `sub`.
   * @return {string|null} A token used for unsubscribing, or `null` if passed an invalid `event`.
   */
  sub(event, callback = () => {}, retroactive = false) {
    if (typeof event !== 'string') {
      return null;
    }

    const [eventName, nameSpace = 'default'] = event.split(':');
    const escapedEventName = eventName.replace(regex, matched => replacementChars[matched]);
    const subPath = _.get(this.subscriptions, `${escapedEventName}[${nameSpace}]`, null);
    const token = `${event}_${Utilities.uuidString()}`;
    if (!subPath) {
      _.set(this.subscriptions, `${escapedEventName}[${nameSpace}]`, []);
    }
    this.subscriptions[`${escapedEventName}`][nameSpace].push({ callback, token });

    if (retroactive && this.alreadyBeenPublished[`${escapedEventName}`]) {
      const args = this.alreadyBeenPublished[`${escapedEventName}`];
      callback({ event, args, data: args });
    }

    return token;
  }

  /**
   * @param {string} event - This can be namespaced with a `:` like
   *   `SPARTA_GLOBAL_READY.spartaBootstrapUtility:foo`.
   * @returns {void}
   */
  unsubByEvent(event) {
    if (!event || typeof (event) !== 'string') {
      return;
    }
    const [eventName, nameSpace] = event.split(':');
    const escapedEventName = eventName.replace(regex, matched => replacementChars[matched]);

    if (!nameSpace) {
      delete this.subscriptions[escapedEventName];
    } else {
      delete this.subscriptions[escapedEventName][nameSpace];
    }
  }

  /**
   * Unsubscribes (removes a listener) from the event registered with the provided token.
   * @param {string} token - The token for the event to unsubscribe from.
   * @return {boolean|null} Returns `false` if no events are found matching the token, `true` if
   *   the event was removed, or `null` if passed an invalid token.
   */
  unsubByToken(token) {
    if (typeof (token) !== 'string' || !token || !token.includes('_')) {
      return null;
    }

    const [eventName, nameSpace = 'default'] = token.split('_')[0].split(':');
    const escapedEventName = eventName.replace(regex, matched => replacementChars[matched]);
    const subscriptions = _.get(this.subscriptions, `${escapedEventName}[${nameSpace}]`, []);

    return subscriptions.some((sub, idx) => {
      if (sub.token === token) {
        subscriptions.splice(idx, 1);
        return true;
      }
      return false;
    });
  }

  /**
   * Unsubscribes (removes a listener) ffrom the event registered with the provided widget token
   * signature.
   * @param {string} token - The token for the event to unsubscribe from.
   * @return {boolean|null} Returns `false` if no events are found matching the token, `true` if
   *   the event was removed, or `null` if passed an invalid token.
   */
  unsubWidgetByToken(token) {
    if (typeof token !== 'string' || !token || !token.includes('_')) {
      return null;
    }

    const eventName = token.substring(0, token.lastIndexOf('_')).replace('.', '/');
    const escapedEventName = eventName.replace(regex, matched => replacementChars[matched]);
    const subscriptions = _.get(this.subscriptions, `${escapedEventName}[default]`, []);

    return subscriptions.some((sub, idx) => {
      if (sub.token === token) {
        subscriptions.splice(idx, 1);
        return true;
      }
      return false;
    });
  }

  /**
   * @param {string} event - This can be namespaced with a `:` like
   *   `SPARTA_GLOBAL_READY.spartaBootstrapUtility:foo`.
   * @param {*} data - Any data to be passed to the callback.
   * @returns {void}
   */
  pub(event, data) {
    if (typeof (event) !== 'string') {
      return;
    }

    /**
     * Starting in 2.2.0, this method is compliant with HTML `CustomEvent`. It now (also)
     * dispatches an HTML `CustomEvent` attached to the document. You can simply do something like
     * `document.addEventListener('eventName', () => ....)` for the event that is published.
     *
     * https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/CustomEvent
     */
    try {
      document.dispatchEvent(new CustomEvent(event, { detail: data }));
    } catch (e) {
      // Fail silently.
    }

    const [eventName, nameSpace] = event.split(':');
    const escapedEventName = eventName.replace(regex, matched => replacementChars[matched]);

    let subscriptions = _.get(this.subscriptions, escapedEventName, {});

    if (nameSpace) {
      subscriptions = _.get(subscriptions, nameSpace, {});
    }

    let callbackMaps = [];

    Object.keys(subscriptions).forEach((sub) => {
      callbackMaps = callbackMaps.concat(subscriptions[sub]);
    });

    this.alreadyBeenPublished[escapedEventName] = data;
    callbackMaps.forEach((obj) => {
      obj.callback({ event, data });
    });
  }
}

export default new SpartaEventsUtility();
